// @ts-nocheck

import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import screenfull from 'screenfull';

import { sendReport } from '../../../actions/report';
import { useShallowEqualSelector } from '../../../hooks';
import useMergeProps from '../../../hooks/useMergeProps';
import { selectEntitiesSportsEntity } from '../../../selectors/entities/sports/entitiesSportsPrimary.selector';
import { selectUserConnected } from '../../../selectors/user/userPrimary.selector';
import ErrorBoundary from '../components/ErrorBoundary';
import ErrorBoundaryFallbackComponent from '../components/ErrorBoundaryFallbackComponent';
import { VideoPlayer } from '../components/video-player';

type VideoPlayerContainerProps = {
  matchId: number;
  streamData: string;
  pause?: boolean;
  isFullscreenAvailable?: boolean;
  isFullscreen?: boolean;
  isMultiview?: boolean;
  isPiPFeatureEnabled?: boolean;
  isVolumeAvailable?: boolean;
  canShowControls?: boolean;
  toggleFullscreen?: () => void;
  onClick?: () => void;
  onMatchStreamExport?: () => void;
  jwt?: string;
  disableExport?: boolean;
  fetchUrl: string | null;
  fetchConfig: unknown | null;
};

const VideoPlayerContainer = (ownProps: VideoPlayerContainerProps) => {
  const { matchId, isFullscreen, fetchUrl, fetchConfig } = ownProps;

  const selectedProps = useShallowEqualSelector((state) => {
    const { token: jwt } = state.user?.jwt ?? {};
    const match = state.entities?.matches?.[matchId];
    const { title, roundName, sportId, tournamentId } = match ?? {};
    const sport = selectEntitiesSportsEntity(state, { sportId });
    const { sportName } = sport ?? {};
    const tournament = state.entities?.tournaments?.[tournamentId];
    const { tournamentName } = tournament ?? {};
    const roundSuffixe = roundName ? `${roundName} | ` : '';

    return {
      jwt: ownProps.jwt || jwt,
      title: `${title} | ${roundSuffixe}${tournamentName} | ${sportName}`,
    };
  });

  const enhancedProps = useMergeProps({ ownProps, selectedProps });

  const exitFullscreen = useCallback(() => {
    screenfull.exit();
  }, []);

  const dispatch = useDispatch();

  const handleError = useCallback(
    (error) => {
      const errorType = error?.name;
      const errorData = error?.data;
      const errorStr = error?.message;
      let fetchBody = undefined;
      try {
        if (fetchConfig) {
          fetchBody = JSON.parse(fetchConfig)?.body;
        }
      } catch (ignored) {
        // ignored
      }

      dispatch(
        sendReport({
          message: 'videoplayer error',
          errorType,
          errorStr,
          errorData,
          matchId,
          fetchUrl,
          fetchBody,
        }),
      );
    },
    [dispatch, fetchConfig, fetchUrl, matchId],
  );

  const isConnected = useSelector(selectUserConnected);

  return (
    <ErrorBoundary
      key={`${isConnected ? 'true' : 'false'}`}
      FallbackComponent={ErrorBoundaryFallbackComponent}
      onClose={exitFullscreen}
      closeable={isFullscreen}
      onError={handleError}
    >
      <VideoPlayer {...enhancedProps} />
    </ErrorBoundary>
  );
};

export default VideoPlayerContainer;
